import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../store/store";
import { fetchProviderById } from "../store/slice/provider/providerThunks";
import { fetchReviewsForProvider } from "../store/slice/review/reviewThunks";
import { createBooking } from "../store/slice/booking/bookingThunks";
import { useAuth } from "../hooks/useAuth";
import LoadMoreButton from "../components/LoadMoreButton";
import { EMPTY_PAGE_INFO } from "../utils/pagination";

const ProviderProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();

  const { selectedProvider, loading, error } = useSelector(
    (state: RootState) => state.provider
  );
  const {
    reviewsByProvider,
    pageInfoByProvider,
    loading: reviewsLoading,
  } = useSelector((state: RootState) => state.review);
  const { loading: bookingLoading, error: bookingError } = useSelector(
    (state: RootState) => state.booking
  );
  const reviews = id ? reviewsByProvider[Number(id)] || [] : [];
  const reviewsPageInfo = (id && pageInfoByProvider[Number(id)]) || EMPTY_PAGE_INFO;
  const reviewCount = Math.max(reviewsPageInfo.totalElements, reviews.length);

  const [location, setLocation] = useState("");
  const [amount, setAmount] = useState("");
  const [bookingDate, setBookingDate] = useState("");
  const [bookingSent, setBookingSent] = useState(false);

  useEffect(() => {
    if (id) {
      dispatch(fetchProviderById(Number(id)));
      dispatch(fetchReviewsForProvider(Number(id)));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (selectedProvider) {
      setLocation(selectedProvider.businessAddress || "");
      setAmount(
        selectedProvider.price != null ? String(selectedProvider.price) : ""
      );
    }
  }, [selectedProvider]);

  const handleBookNow = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !user?.id || !selectedProvider) return;

    const result = await dispatch(
      createBooking({
        customerId: user.id,
        handymanId: Number(id),
        serviceType: selectedProvider.service?.name || "General",
        location,
        amount: Number(amount),
        bookingDate: new Date(bookingDate).toISOString(),
      })
    );
    if (result) {
      setBookingSent(true);
    }
  };

  if (loading) {
    return <p className="text-center mt-10">Loading provider...</p>;
  }

  if (error || !selectedProvider) {
    return (
      <p className="text-center text-red-600 mt-10">
        {error || "Provider not found."}
      </p>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-8">
      <div className="bg-white shadow-lg rounded-lg p-6">
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-2xl font-bold text-gray-900">
            {selectedProvider.businessName || selectedProvider.fullName}
          </h1>
          {selectedProvider.verificationStatus === "VERIFIED" && (
            <span className="inline-flex items-center gap-1 bg-green-100 text-green-800 text-xs font-semibold px-2.5 py-1 rounded-full">
              ✓ Verified
            </span>
          )}
        </div>
        <p className="text-gray-500">{selectedProvider.fullName}</p>

        <div className="mt-3 flex items-center gap-2">
          <span className="text-yellow-500 text-lg">
            ⭐ {selectedProvider.rating?.toFixed(1) ?? "0.0"}
          </span>
          <span className="text-sm text-gray-500">
            ({reviewCount} review{reviewCount === 1 ? "" : "s"})
          </span>
        </div>

        <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          {selectedProvider.service && (
            <div>
              <dt className="text-gray-500">Service</dt>
              <dd className="font-medium text-gray-900">
                {selectedProvider.service.name}
              </dd>
            </div>
          )}
          {selectedProvider.businessAddress && (
            <div>
              <dt className="text-gray-500">Address</dt>
              <dd className="font-medium text-gray-900">
                {selectedProvider.businessAddress}
              </dd>
            </div>
          )}
          {selectedProvider.phone && (
            <div>
              <dt className="text-gray-500">Phone</dt>
              <dd className="font-medium text-gray-900">
                <a
                  href={`tel:${selectedProvider.phone}`}
                  className="text-green-600 hover:underline"
                >
                  {selectedProvider.phone}
                </a>
              </dd>
            </div>
          )}
          {selectedProvider.price != null && (
            <div>
              <dt className="text-gray-500">Price</dt>
              <dd className="font-medium text-gray-900">
                ₦{selectedProvider.price}
              </dd>
            </div>
          )}
          {selectedProvider.experience && (
            <div>
              <dt className="text-gray-500">Experience</dt>
              <dd className="font-medium text-gray-900">
                {selectedProvider.experience}
              </dd>
            </div>
          )}
        </dl>

        {selectedProvider.bio && (
          <p className="mt-4 text-gray-700">{selectedProvider.bio}</p>
        )}
      </div>

      <div className="bg-white shadow-lg rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">
          Book {selectedProvider.businessName || selectedProvider.fullName}
        </h2>

        {!isAuthenticated ? (
          <p className="text-sm text-gray-600">
            <button
              onClick={() => navigate("/login")}
              className="text-green-700 font-medium hover:underline"
            >
              Log in
            </button>{" "}
            to book this provider.
          </p>
        ) : bookingSent ? (
          <div className="text-sm">
            <p className="text-green-700 font-medium mb-2">
              Booking request sent ✓
            </p>
            <button
              onClick={() => navigate("/dashboard/customer")}
              className="text-green-700 hover:underline"
            >
              View my bookings
            </button>
          </div>
        ) : (
          <form onSubmit={handleBookNow} className="space-y-4">
            {bookingError && (
              <p className="text-red-600 text-sm">{bookingError}</p>
            )}
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-900">
                Location
              </label>
              <input
                type="text"
                required
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="w-full p-2.5 text-sm rounded-lg border border-gray-300"
              />
            </div>
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-900">
                Amount (₦)
              </label>
              <input
                type="number"
                required
                min="0"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full p-2.5 text-sm rounded-lg border border-gray-300"
              />
            </div>
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-900">
                When do you need this?
              </label>
              <input
                type="datetime-local"
                required
                value={bookingDate}
                onChange={(e) => setBookingDate(e.target.value)}
                className="w-full p-2.5 text-sm rounded-lg border border-gray-300"
              />
            </div>
            <button
              type="submit"
              disabled={bookingLoading}
              className="w-full py-2.5 px-5 text-sm font-medium text-white rounded-lg bg-green-600 hover:bg-green-700 disabled:bg-gray-400"
            >
              {bookingLoading ? "Sending request..." : "Book Now"}
            </button>
          </form>
        )}
      </div>

      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Reviews</h2>
        {reviews.length === 0 ? (
          <p className="text-gray-500 text-sm">
            No reviews yet for this provider.
          </p>
        ) : (
          <ul className="space-y-3">
            {reviews.map((r) => (
              <li
                key={r.id}
                className="border border-gray-200 rounded-lg p-3 bg-white"
              >
                <div className="flex items-center justify-between">
                  <span className="text-yellow-500 font-semibold">
                    {"⭐".repeat(r.rating)}
                  </span>
                  <span className="text-xs text-gray-400">
                    {new Date(r.createdAt).toLocaleDateString()}
                  </span>
                </div>
                {r.comment && (
                  <p className="mt-1 text-sm text-gray-700">{r.comment}</p>
                )}
              </li>
            ))}
          </ul>
        )}
        <LoadMoreButton
          shown={reviews.length}
          total={reviewsPageInfo.totalElements}
          last={reviewsPageInfo.last}
          loading={reviewsLoading}
          onClick={() =>
            id && dispatch(fetchReviewsForProvider(Number(id), reviewsPageInfo.page + 1))
          }
        />
      </div>
    </div>
  );
};

export default ProviderProfile;
